const SIZE_CONVERTER_MISSING_LABEL = '–';

class SizeConverter extends HTMLElement {
  connectedCallback() {
    this.fields = Array.from(this.querySelectorAll('[data-size-field]'));
    this.calculateButton = this.querySelector('[data-calculate]');
    this.resetButton = this.querySelector('[data-reset]');
    this.errorEl = this.querySelector('[data-error]');
    this.activeField = null;

    this.fields.forEach((field) => {
      const input = field.querySelector('[data-input]');
      if (!input) return;
      input.addEventListener('input', () => this.onInput(field, input));
    });

    if (this.calculateButton) {
      this.calculateButton.addEventListener('click', () => this.calculate());
    }
    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this.reset());
    }
  }

  isMissing(value) {
    if (value === null || value === undefined) return true;
    const trimmed = String(value).trim();
    return trimmed === '' || trimmed === '-' || trimmed === SIZE_CONVERTER_MISSING_LABEL;
  }

  parseNumber(value) {
    if (this.isMissing(value)) return NaN;

    let str = String(value)
      .replace(',', '.')
      .replace(/[a-zA-Z]+/g, '')
      .trim();

    const mixed = str.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) {
      return parseInt(mixed[1], 10) + parseInt(mixed[2], 10) / parseInt(mixed[3], 10);
    }

    const fraction = str.match(/^(\d+)\/(\d+)$/);
    if (fraction) {
      return parseInt(fraction[1], 10) / parseInt(fraction[2], 10);
    }

    const number = parseFloat(str);
    return isNaN(number) ? NaN : number;
  }

  getCells(field) {
    try {
      const parsed = JSON.parse(field.dataset.values || '[]');
      return parsed.map((entry) => String(entry).trim());
    } catch (error) {
      return [];
    }
  }

  getInput(field) {
    return field.querySelector('[data-input]');
  }

  getBounds(field) {
    const cells = this.getCells(field);
    const present = cells
      .map((cell) => ({ cell, number: this.parseNumber(cell) }))
      .filter((entry) => !isNaN(entry.number))
      .sort((a, b) => a.number - b.number);

    if (!present.length) return null;

    const min = present[0];
    const max = present[present.length - 1];
    return {
      minNumber: min.number,
      maxNumber: max.number,
      minLabel: min.cell,
      maxLabel: max.cell,
    };
  }

  onInput(field, input) {
    const sanitized = input.value.replace(/[^0-9.,]/g, '');
    if (sanitized !== input.value) {
      input.value = sanitized;
    }

    this.clearError();
    this.clearSuccess();

    const hasValue = input.value.trim() !== '';
    this.activeField = hasValue ? field : null;

    this.fields.forEach((otherField) => {
      if (otherField === field) return;
      const otherInput = this.getInput(otherField);
      if (!otherInput) return;
      otherInput.disabled = hasValue;
      if (hasValue) {
        otherInput.value = '';
      }
    });
  }

  calculate() {
    const field =
      this.activeField ||
      this.fields.find((candidate) => {
        const input = this.getInput(candidate);
        return input && input.value.trim() !== '';
      });

    if (!field) return;

    const input = this.getInput(field);
    const value = this.parseNumber(input.value);
    const bounds = this.getBounds(field);
    if (!bounds) return;

    if (isNaN(value) || value < bounds.minNumber || value > bounds.maxNumber) {
      this.showError(field, bounds);
      return;
    }

    this.clearError();

    const cells = this.getCells(field);
    let matchIndex = -1;
    let smallestDistance = Infinity;
    cells.forEach((cell, index) => {
      const number = this.parseNumber(cell);
      if (isNaN(number)) return;
      const distance = Math.abs(number - value);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        matchIndex = index;
      }
    });

    if (matchIndex < 0) return;

    // Eingabe auf den tatsächlich getroffenen Listenwert normalisieren (7,2 -> 7)
    const matchedCell = cells[matchIndex];
    if (!this.isMissing(matchedCell)) {
      input.value = matchedCell;
    }

    this.fields.forEach((otherField) => {
      if (otherField === field) return;
      const otherInput = this.getInput(otherField);
      if (!otherInput) return;

      const otherCells = this.getCells(otherField);
      const raw = otherCells[matchIndex];

      if (raw === undefined || this.isMissing(raw)) {
        otherInput.value = SIZE_CONVERTER_MISSING_LABEL;
        otherInput.classList.remove('size-converter__input--success');
      } else {
        otherInput.value = raw;
        otherInput.classList.add('size-converter__input--success');
      }
    });
  }

  clearSuccess() {
    this.fields.forEach((field) => {
      const input = this.getInput(field);
      if (input) input.classList.remove('size-converter__input--success');
    });
  }

  showError(field, bounds) {
    const input = this.getInput(field);
    const range = bounds || this.getBounds(field) || { minLabel: '', maxLabel: '' };
    let message = field.dataset.errorText || '';
    message = message
      .replace(/\{min\}/g, range.minLabel || '')
      .replace(/\{max\}/g, range.maxLabel || '');

    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = message.trim() === '';
    }
    if (input) {
      input.setAttribute('aria-invalid', 'true');
    }
  }

  clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
    this.fields.forEach((field) => {
      const input = this.getInput(field);
      if (input) input.removeAttribute('aria-invalid');
    });
  }

  reset() {
    this.activeField = null;
    this.clearSuccess();
    this.clearError();
    this.fields.forEach((field) => {
      const input = this.getInput(field);
      if (input) {
        input.value = '';
        input.disabled = false;
      }
    });
  }
}

if (!customElements.get('size-converter')) {
  customElements.define('size-converter', SizeConverter);
}
